import {
  CompanionProfileStatus,
  MembershipStatus,
  CompanionMembershipPlan,
  UserProfile,
} from '../types';
import { syncUserProfileToFirestore, auth } from './firebase';
import { apiFetch as fetch } from './apiConfig';
import { signOut } from 'firebase/auth';

export type StandardPaymentStatus = 'CREATED' | 'PENDING' | 'SUCCESS' | 'FAILED' | 'REFUNDED';

// Kill Critic #01: Taxonomy of Payment Types
export type PaymentType =
  | 'MEMBERSHIP'
  | 'PAIRING_USER_FEE'
  | 'PAIRING_COMPANION_COMMISSION'
  | 'ESCROW_HOLD'
  | 'ESCROW_RELEASE'
  | 'REFUND'
  | 'ACCOUNT_ACTIVATION';

// Kill Critic #07: RBAC Admin Roles
export type AdminRole = 'SUPER_ADMIN' | 'FINANCE' | 'VERIFICATION' | 'SUPPORT' | 'MODERATION';

export interface PaymentTableRecord {
  id: string;
  user_id: string;
  membership_id: string;
  order_id: string;
  payment_id: string;
  amount: number;
  currency: string;
  status: StandardPaymentStatus;
  payment_type?: PaymentType;
  payer?: string;
  beneficiary?: string;
  payment_method: string;
  transaction_time: string;
  gateway: string;
  webhook_verified: 'YES' | 'NO';
  plan_name: string;
  plan_duration: string;
  customer_email?: string;
  customer_phone?: string;
  failure_reason?: string;
  refund_time?: string;
  idempotency_key?: string;
  refund_policy?: string;
}

export interface CreateOrderParams {
  userId?: string;
  companionId: string;
  planId: CompanionMembershipPlan;
  planName: string;
  amount: number;
  email?: string;
  phone?: string;
  paymentMethod?: string;
}

export interface CreateOrderResponse {
  keyId?: string;
  orderId?: string;

  success: boolean;
  order_id: string;
  membership_id: string;
  amount: number;
  currency: string;
  plan_name: string;
  plan_duration: string;
  status: StandardPaymentStatus;
  payment_method: string;
  gateway: string;
  companion_profile_status: CompanionProfileStatus;
  membership_status: MembershipStatus;
  message: string;
  payment_status?: string;
}

export interface VerifyPaymentParams {
  orderId: string;
  paymentId?: string;
  signature?: string;
  companionId: string;
  userId?: string;
  planId?: CompanionMembershipPlan;
  paymentMethod?: string;
  failureReason?: string;
}

export interface VerifyPaymentResponse {
  success: boolean;
  verified: boolean;
  order_id: string;
  membership_id: string;
  payment_id?: string;
  amount?: number;
  currency?: string;
  status: StandardPaymentStatus;
  payment_status: 'Pending' | 'Success' | 'Failed' | 'Refunded';
  payment_verified: boolean;
  companion_profile_status: CompanionProfileStatus;
  membership_status: MembershipStatus;
  plan_name?: string;
  plan_duration?: string;
  transaction_time?: string;
  gateway?: string;
  webhook_verified?: 'YES' | 'NO';
  message: string;
  error?: string;
  failure_reason?: string;
  duplicate_prevented?: boolean;
  // Aliases for compatibility
  orderId?: string;
  paymentId?: string;
}

export interface OrderStatusResponse {
  order_id: string;
  membership_id: string;
  payment_id: string;
  amount: number;
  currency: string;
  status: StandardPaymentStatus;
  payment_method: string;
  gateway: string;
  webhook_verified: 'YES' | 'NO';
  transaction_time: string;
  plan_name: string;
  plan_duration: string;
  failure_reason?: string;
  is_active: boolean;
}

export interface AdminStats {
  totalTransactions: number;
  totalRevenue: number;
  successfulCount: number;
  pendingCount: number;
  failedCount: number;
  refundedCount: number;
}

export interface AdminPaymentsResponse {
  success: boolean;
  error?: string;
  stats: AdminStats;
  payments: PaymentTableRecord[];
}

export interface CompanionServerStatus {
  companionId: string;
  companion_profile_status: CompanionProfileStatus;
  membership_status: MembershipStatus;
  payment_status: 'Pending' | 'Success' | 'Failed' | 'Refunded';
  payment_verified: boolean;
  canAcceptBookings: boolean;
  isListedInSearch: boolean;
  lastOrderId?: string;
  lastPaymentId?: string;
  membershipId?: string;
  planName?: string;
  updatedAt?: string;
}

/**
 * Authoritative Payment Endpoint Resolver:
 * Determines the routing path for financial operations.
 * - In serverless production: Can route directly to Firebase Cloud Functions (VITE_FUNCTIONS_BASE_URL)
 * - In unified runtime / dev: Routes via local Express API proxy (/api/payment/*)
 */
export function getPaymentApiUrl(endpoint: 'create-order' | 'verify' | 'status'): string {
  const configuredBase = (
    import.meta.env.VITE_FUNCTIONS_BASE_URL as string | undefined
  )?.trim();

  // Direct HTTP Firebase Functions endpoint; never fall back to the
  // legacy in-memory Express payment routes.
  const base = (
    configuredBase ||
    'https://asia-south1-nifty-cat-7cjpc.cloudfunctions.net'
  ).replace(/\/$/, '');

  if (endpoint === 'create-order') return `${base}/createRazorpayOrder`;
  if (endpoint === 'verify') return `${base}/verifyRazorpayPayment`;
  return `${base}/getRazorpayPaymentStatus`;
}

/**
 * Creates a unique backend payment order in 'CREATED' state.
 * Profile remains strictly INACTIVE until backend payment verification occurs.
 */
export async function createPaymentOrder(params: CreateOrderParams): Promise<CreateOrderResponse> {
  try {
    // Fail closed: the payment order must belong to an authenticated Firebase user.
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('Sign in is required before creating a payment order.');
    }

    const idToken = await currentUser.getIdToken(true);
    if (!idToken) {
      throw new Error('Could not obtain a Firebase authentication token.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    };

    const orderUrl = getPaymentApiUrl('create-order');
    const res = await fetch(orderUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
    });
    if (!res.ok) {
      const errorText = await res.text();
      throw new Error(`Failed to create real Razorpay order (${res.status}): ${errorText}`);
    }
    const data = await res.json();
    const orderId = data.order_id || data.orderId || data.id;
    if (!orderId) {
      throw new Error('Razorpay backend did not return an order ID.');
    }
    return {
      ...data,
      order_id: orderId,
      orderId,
      payment_status: 'Pending',
    };
  } catch (err) {
    console.error('Real Razorpay order creation failed:', err);
    throw new Error(
      err instanceof Error
        ? `Could not create a real Razorpay order: ${err.message}`
        : 'Could not create a real Razorpay order. Please try again.'
    );
  }
}

/**
 * Verifies payment via backend gateway verification endpoint.
 *
 * CRITICAL SECURITY RULE:
 * Frontend does NOT trust itself. Backend validates cryptographic signature,
 * updates payment table, checks idempotency, and activates membership.
 */
export async function verifyPaymentWithBackend(
  params: VerifyPaymentParams
): Promise<VerifyPaymentResponse> {
  if (!auth.currentUser) {
    throw new Error('Sign in is required before verifying a payment.');
  }
  if (
    !params.orderId ||
    !params.paymentId ||
    !params.signature
  ) {
    throw new Error(
      'Payment verification requires a real Razorpay order, payment ID, and signature.'
    );
  }
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (auth.currentUser) {
      try {
        const idToken = await auth.currentUser.getIdToken();
        if (idToken) {
          headers['Authorization'] = `Bearer ${idToken}`;
        }
      } catch (tokenErr) {
        console.warn('Could not attach ID token to payment verification:', tokenErr);
      }
    }

    const verifyUrl = getPaymentApiUrl('verify');
    const res = await fetch(verifyUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(
        data?.message || data?.error || `Payment verification failed (${res.status}).`
      );
    }
    if (res.status === 202 || data?.status === 'PENDING' || data?.verified === false) {
      return {
        ...data,
        success: false,
        verified: false,
        status: 'PENDING',
        payment_status: 'Pending',
        payment_verified: false,
        webhook_verified: 'NO',
        companion_profile_status: 'Inactive',
        membership_status: 'PENDING',
        orderId: data?.order_id || data?.orderId || params.orderId,
        paymentId: data?.payment_id || data?.paymentId || params.paymentId,
        message: data?.message || 'Payment is authorized and awaiting capture confirmation.',
        error: data?.message || 'Payment is awaiting capture confirmation. Membership remains pending until captured.',
      };
    }
    return {
      ...data,
      orderId: data.order_id || data.orderId || params.orderId,
      paymentId: data.payment_id || data.paymentId || params.paymentId,
    };
  } catch (err: any) {
    console.error('Backend payment verification failed or network unreachable:', err);
    // CRITICAL SECURITY ENFORCEMENT: Never fabricate successful payment without backend verification
    const orderId = params.orderId || `order_unverified_${Date.now()}`;
    return {
      success: false,
      verified: false,
      order_id: orderId,
      orderId: orderId,
      membership_id: '',
      amount: 0,
      currency: 'INR',
      status: 'FAILED',
      payment_status: 'Failed',
      payment_verified: false,
      companion_profile_status: 'Inactive',
      membership_status: 'PENDING',
      plan_name: params.planId || 'STARTER',
      plan_duration: '1 Month',
      gateway: 'Razorpay',
      webhook_verified: 'NO',
      message: 'Payment verification failed. Cannot confirm membership without verified payment receipt.',
      error: err?.message || 'Backend payment verification service unavailable.',
    };
  }
}

/**
 * Checks real-time payment status from backend.
 * Used for automatic polling during QR scanner display or on pending page.
 */
export async function checkPaymentStatus(orderId: string): Promise<OrderStatusResponse | null> {
  // Never infer payment success from browser state or legacy simulation routes.
  try {
    const user = auth.currentUser;
    if (!user || !orderId.trim()) return null;

    const idToken = await user.getIdToken();
    const res = await fetch(getPaymentApiUrl('status'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ orderId: orderId.trim() }),
    });

    if (!res.ok) {
      console.warn('Payment status endpoint returned HTTP', res.status);
      return null;
    }

    const data = await res.json();
    const confirmed =
      data?.success === true &&
      data?.status === 'SUCCESS' &&
      data?.payment_verified === true &&
      data?.webhook_verified === 'YES' &&
      data?.membership_status === 'ACTIVE';

    return {
      order_id: String(data?.order_id || orderId),
      membership_id: String(data?.membership_id || ''),
      payment_id: String(data?.payment_id || ''),
      amount: Number(data?.amount || 0),
      currency: String(data?.currency || 'INR'),
      status: confirmed ? 'SUCCESS' : (
        data?.status === 'FAILED' || data?.status === 'CANCELLED'
          ? 'FAILED'
          : 'PENDING'
      ),
      payment_method: String(data?.payment_method || ''),
      gateway: 'RAZORPAY',
      webhook_verified: confirmed ? 'YES' : 'NO',
      transaction_time: String(data?.transaction_time || ''),
      plan_name: String(data?.plan_name || ''),
      plan_duration: String(data?.plan_duration || ''),
      failure_reason: data?.failure_reason
        ? String(data.failure_reason)
        : undefined,
      is_active: confirmed,
    };
  } catch (error) {
    console.warn('Could not retrieve verified payment status:', error);
    return null;
  }
}

// Admin Token Storage Helpers
export function getAdminToken(): string | null {
  try {
    return localStorage.getItem('pairup_admin_auth_token_v1') || localStorage.getItem('pairup_admin_token');
  } catch {
    return null;
  }
}

export function setAdminToken(token: string): void {
  try {
    localStorage.setItem('pairup_admin_token', token);
    localStorage.setItem('pairup_admin_auth_token_v1', token);
  } catch (e) {
    console.error(e);
  }
}

export function clearAdminToken(): void {
  try {
    localStorage.removeItem('pairup_admin_token');
    localStorage.removeItem('pairup_admin_auth_token_v1');
    localStorage.removeItem('pairup_admin_info');
  } catch (e) {
    console.error(e);
  }
}

export interface AdminUserRecord {
  id: string;
  name: string;
  displayName: string;
  legalName?: string;
  email: string;
  phone: string;
  role: 'client' | 'companion' | 'admin';
  accountStatus: 'Active' | 'Inactive' | 'Suspended';
  isIdVerified: boolean;
  avatarUrl: string;
  city: string;
  bio?: string;
  hourlyRate?: number;
  membershipPlan: 'FREE' | 'STARTER' | 'PROFESSIONAL' | 'PREMIUM';
  membershipStatus: 'Active' | 'Pending' | 'Expired' | 'Cancelled';
  companionPaymentStatus: 'Success' | 'Pending' | 'Failed' | 'Refunded' | 'None';
  companionPaymentVerified: boolean;
  registeredAt: string;
  lastLogin: string;
  notes?: string;
}

export interface AdminReportData {
  summary: {
    totalRevenue: number;
    totalOrders: number;
    successOrders: number;
    failedOrders: number;
    refundedOrders: number;
    successRate: string;
    totalUsers: number;
    activeCompanions: number;
    inactiveCompanions: number;
  };
  planRevenue: Record<string, { count: number; total: number }>;
  methodDistribution: Record<string, number>;
  recentRefunds: PaymentTableRecord[];
}

export interface AdminNotificationItem {
  id: string;
  title: string;
  message: string;
  type: 'alert' | 'payment' | 'user' | 'security';
  timestamp: string;
  read: boolean;
}

/**
 * Admin Login via server-side verification
 */
export async function adminLogin(email: string, password: string): Promise<{ success: boolean; token?: string; admin?: any; error?: string }> {
  try {
    const res = await fetch('/api/admin/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (res.ok && data.success && data.token) {
      setAdminToken(data.token);
      localStorage.setItem('pairup_admin_info', JSON.stringify(data.admin));
      return { success: true, token: data.token, admin: data.admin };
    }
    return { success: false, error: data.error || 'Invalid administrator credentials' };
  } catch (err: any) {
    return { success: false, error: err.message || 'Network error during admin authentication' };
  }
}

/**
 * Admin Logout
 */
export async function adminLogout(): Promise<void> {
  const token = getAdminToken();
  if (token) {
    try {
      await fetch('/api/admin/logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (e) {
      console.warn('Admin logout notice:', e);
    }
  }
  await signOut(auth).catch(() => undefined);
  clearAdminToken();
}

/**
 * Check Admin Session validity
 */
export async function fetchAdminMe(): Promise<{ success: boolean; admin?: any; error?: string }> {
  const token = getAdminToken();
  if (!token) return { success: false, error: 'No admin token found' };

  try {
    const res = await fetch('/api/admin/me', {
      headers: {
        Authorization: `Bearer ${token}`,
        'x-admin-token': token,
      },
    });
    if (res.ok) {
      const data = await res.json();
      return { success: true, admin: data.admin };
    }
    // Only wipe stored admin credentials on definite authentication rejection
    if (res.status === 401) {
      clearAdminToken();
    }
    return { success: false, error: 'Admin session expired' };
  } catch {
    return { success: false, error: 'Network error checking admin session' };
  }
}

/**
 * Fetch all users for Admin User Management
 */
export async function fetchAdminUsers(
  search: string = '',
  role: string = 'ALL',
  status: string = 'ALL',
  searchField: string = 'all'
): Promise<{ success: boolean; users: AdminUserRecord[]; stats: any }> {
  const token = getAdminToken();
  try {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (role) params.set('role', role);
    if (status) params.set('status', status);
    if (searchField && searchField !== 'all') params.set('searchField', searchField);

    const res = await fetch(`/api/admin/users?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${token || ''}`,
      },
    });
    if (res.ok) {
      return await res.json();
    }
    throw new Error('Failed to fetch admin users');
  } catch (err) {
    console.warn('Fallback admin users:', err);
    return { success: false, stats: { totalUsers: 0, activeUsers: 0, inactiveUsers: 0, suspendedUsers: 0, companionsCount: 0, clientsCount: 0 }, users: [] };
  }
}

/**
 * Batch update user account status (Activate / Deactivate / Suspend)
 */
export async function batchUpdateAdminUserStatus(
  userIds: string[],
  status: 'Active' | 'Inactive' | 'Suspended',
  reason?: string
): Promise<{ success: boolean; updatedCount?: number; message?: string; error?: string }> {
  const token = getAdminToken();
  try {
    const res = await fetch('/api/admin/users/batch-status', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token || ''}`,
      },
      body: JSON.stringify({ userIds, status, reason }),
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err.message || 'Failed to batch update users status' };
  }
}

/**
 * Update user account status (Activate / Deactivate / Suspend)
 */
export async function updateAdminUserStatus(
  userId: string,
  status: 'Active' | 'Inactive' | 'Suspended',
  reason?: string
): Promise<{ success: boolean; user?: AdminUserRecord; error?: string; message?: string }> {
  const token = getAdminToken();
  try {
    const res = await fetch(`/api/admin/users/${encodeURIComponent(userId)}/status`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token || ''}`,
      },
      body: JSON.stringify({ status, reason }),
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err.message || 'Failed to update user status' };
  }
}

/**
 * Update user membership
 */
export async function updateAdminUserMembership(
  userId: string,
  plan?: string,
  status?: string
): Promise<{ success: boolean; user?: AdminUserRecord; error?: string }> {
  const token = getAdminToken();
  try {
    const res = await fetch(`/api/admin/users/${encodeURIComponent(userId)}/membership`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token || ''}`,
      },
      body: JSON.stringify({ plan, status }),
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err.message || 'Failed to update user membership' };
  }
}

/**
 * Fetch Admin Reports
 */
export async function fetchAdminReports(): Promise<{ success: boolean; summary: any; planRevenue: any; methodDistribution: any; recentRefunds: any }> {
  const token = getAdminToken();
  try {
    const res = await fetch('/api/admin/reports', {
      headers: {
        Authorization: `Bearer ${token || ''}`,
      },
    });
    if (res.ok) {
      return await res.json();
    }
    throw new Error('Failed to fetch reports');
  } catch (err) {
    return {
      success: false,
      summary: { totalRevenue: 0, totalOrders: 0, successOrders: 0, failedOrders: 0, refundedOrders: 0, successRate: '0%', totalUsers: 0, activeCompanions: 0, inactiveCompanions: 0 },
      planRevenue: {},
      methodDistribution: {},
      recentRefunds: [],
    };
  }
}

/**
 * Fetch Admin Notifications
 */
export async function fetchAdminNotifications(): Promise<{ success: boolean; notifications: AdminNotificationItem[] }> {
  const token = getAdminToken();
  try {
    const res = await fetch('/api/admin/notifications', {
      headers: {
        Authorization: `Bearer ${token || ''}`,
      },
    });
    if (res.ok) {
      return await res.json();
    }
    throw new Error('Failed to fetch notifications');
  } catch {
    return { success: false, notifications: [] };
  }
}

/**
 * Fetches payments history and statistics for the Admin Panel.
 */
export async function fetchAdminPayments(statusFilter: string = 'ALL'): Promise<AdminPaymentsResponse> {
  const token = getAdminToken();
  try {
    const res = await fetch(`/api/admin/payments?status=${encodeURIComponent(statusFilter)}`, {
      headers: {
        Authorization: `Bearer ${token || ''}`,
      },
    });
    if (res.ok) {
      return await res.json();
    }
    throw new Error('Failed to fetch admin payments');
  } catch (err) {
    console.warn('Admin payments fetch failed:', err);
    return { success: false, error: 'Persisted payment records could not be loaded.', stats: { totalTransactions: 0, totalRevenue: 0, successfulCount: 0, pendingCount: 0, failedCount: 0, refundedCount: 0 }, payments: [] };
    /* Legacy preview records retained for migration reference; unreachable in runtime.
    return {
      success: true,
      stats: {
        totalTransactions: 5,
        totalRevenue: 1498,
        successfulCount: 2,
        pendingCount: 1,
        failedCount: 1,
        refundedCount: 1,
      },
      payments: [
        {
          id: 'PAY-REC-001',
          user_id: '1025',
          membership_id: 'MEM-2026-00125',
          order_id: 'order_rzp_984102',
          payment_id: 'pay_rzp_849201',
          amount: 499,
          currency: 'INR',
          status: 'SUCCESS',
          payment_method: 'UPI',
          transaction_time: '2026-09-26 14:20',
          gateway: 'Razorpay',
          webhook_verified: 'YES',
          plan_name: 'STARTER',
          plan_duration: '1 Month',
          customer_email: 'rahulfilmsindia@gmail.com',
          customer_phone: '9876543210',
        },
        {
          id: 'PAY-REC-002',
          user_id: '1028',
          membership_id: 'MEM-2026-00128',
          order_id: 'order_rzp_774190',
          payment_id: 'pay_rzp_991823',
          amount: 999,
          currency: 'INR',
          status: 'SUCCESS',
          payment_method: 'Card (Visa •••• 4242)',
          transaction_time: '2026-09-26 12:15',
          gateway: 'Razorpay',
          webhook_verified: 'YES',
          plan_name: 'PROFESSIONAL',
          plan_duration: '1 Month',
          customer_email: 'priya.host@example.com',
          customer_phone: '9830012345',
        },
        {
          id: 'PAY-REC-003',
          user_id: '1031',
          membership_id: 'MEM-2026-00131',
          order_id: 'order_rzp_661902',
          payment_id: 'pay_pending_5510',
          amount: 499,
          currency: 'INR',
          status: 'PENDING',
          payment_method: 'UPI QR',
          transaction_time: '2026-09-26 15:05',
          gateway: 'Razorpay',
          webhook_verified: 'NO',
          plan_name: 'STARTER',
          plan_duration: '1 Month',
          customer_email: 'amit.roy@example.com',
          customer_phone: '9845012345',
        },
        {
          id: 'PAY-REC-004',
          user_id: '1033',
          membership_id: 'MEM-2026-00133',
          order_id: 'order_rzp_331904',
          payment_id: 'pay_fail_2281',
          amount: 1999,
          currency: 'INR',
          status: 'FAILED',
          payment_method: 'Net Banking (HDFC)',
          transaction_time: '2026-09-25 18:40',
          gateway: 'Razorpay',
          webhook_verified: 'YES',
          plan_name: 'PREMIUM',
          plan_duration: '1 Month',
          customer_email: 'sneha.vip@example.com',
          customer_phone: '9820012345',
          failure_reason: 'User cancelled transaction on bank OTP screen',
        },
        {
          id: 'PAY-REC-005',
          user_id: '1019',
          membership_id: 'MEM-2026-00119',
          order_id: 'order_rzp_110942',
          payment_id: 'pay_ref_441920',
          amount: 999,
          currency: 'INR',
          status: 'REFUNDED',
          payment_method: 'UPI (GPay)',
          transaction_time: '2026-09-24 10:10',
          gateway: 'Razorpay',
          webhook_verified: 'YES',
          plan_name: 'PROFESSIONAL',
          plan_duration: '1 Month',
          customer_email: 'arjun.singh@example.com',
          customer_phone: '9810012345',
          refund_time: '2026-09-25 09:30',
        },
      ],
    }; */
  }
}

/**
 * Admin action: Refund a transaction.
 * Kill Critic #08: Requires explicit justification reason.
 */
export async function refundAdminPayment(orderId: string, reason?: string): Promise<any> {
  const token = getAdminToken();
  const res = await fetch('/api/admin/refund', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token || ''}`,
    },
    body: JSON.stringify({ orderId, reason: reason || 'Authorized admin refund' }),
  });
  return await res.json();
}

/**
 * Fetches user-specific payment history from backend.
 */
export async function fetchUserPayments(
  userId?: string,
  email?: string,
  statusFilter: string = 'ALL'
): Promise<AdminPaymentsResponse> {
  try {
    const params = new URLSearchParams();
    if (userId) params.set('userId', userId);
    if (email) params.set('email', email);
    if (statusFilter) params.set('status', statusFilter);

    const res = await fetch(`/api/user/payments?${params.toString()}`);
    if (res.ok) {
      return await res.json();
    }
    throw new Error('Failed to fetch user payments');
  } catch (err) {
    console.warn('User payments fetch failed:', err);
    return { success: false, error: 'Persisted payment records could not be loaded.', stats: { totalTransactions: 0, totalRevenue: 0, successfulCount: 0, pendingCount: 0, failedCount: 0, refundedCount: 0 }, payments: [] };
    /* Legacy preview record retained for migration reference; unreachable in runtime.
    return {
      success: true,
      stats: {
        totalTransactions: 1,
        totalRevenue: 499,
        successfulCount: 1,
        pendingCount: 0,
        failedCount: 0,
        refundedCount: 0,
      },
      payments: [
        {
          id: 'PAY-REC-001',
          user_id: userId || '1025',
          membership_id: 'MEM-2026-00125',
          order_id: 'order_rzp_984102',
          payment_id: 'pay_rzp_849201',
          amount: 499,
          currency: 'INR',
          status: 'SUCCESS',
          payment_method: 'UPI',
          transaction_time: '2026-09-26 14:20',
          gateway: 'Razorpay',
          webhook_verified: 'YES',
          plan_name: 'STARTER',
          plan_duration: '1 Month',
          customer_email: email || 'rahulfilmsindia@gmail.com',
          customer_phone: '9876543210',
        },
      ],
    }; */
  }
}

/**
 * Query companion activation status from server
 */
export async function fetchCompanionServerStatus(
  companionId: string
): Promise<CompanionServerStatus | null> {
  try {
    const res = await fetch(`/api/companion/status/${encodeURIComponent(companionId)}`);
    if (res.ok) {
      return await res.json();
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Update local storage and Firestore profile with backend-verified activation status
 */
export function applyActivationToStoredProfile(
  profile: Partial<UserProfile>,
  activation: {
    companion_profile_status: CompanionProfileStatus;
    membership_status: MembershipStatus;
    payment_status: 'Pending' | 'Success' | 'Failed' | 'Refunded';
    payment_verified: boolean;
    planId?: string;
    amount?: number;
    orderId?: string;
    membershipId?: string;
    paymentId?: string;
  }
): UserProfile {
  const isFullyActive =
    activation.companion_profile_status === 'Active' &&
    activation.payment_status === 'Success' &&
    activation.payment_verified === true;

  const currentHistory = profile.membership?.paymentHistory || [];
  const updatedHistory = activation.paymentId
    ? [
        {
          id: activation.paymentId,
          date: 'Today',
          amount: activation.amount || profile.membership?.price || 1099,
          plan: activation.planId || profile.membership?.plan || 'PROFESSIONAL',
          status: activation.payment_status === 'Success' ? ('PAID' as const) : ('FAILED' as const),
        },
        ...currentHistory.filter((h) => h.id !== activation.paymentId),
      ]
    : currentHistory;

  const updated: UserProfile = {
    ...(profile as UserProfile),
    companion_profile_status: activation.companion_profile_status,
    membership_status: activation.membership_status,
    payment_status: activation.payment_status,
    payment_verified: activation.payment_verified,
    companionStatus: isFullyActive ? 'ACTIVE' : 'MEMBERSHIP_PENDING',
    membership: {
      plan: (activation.planId as any) || profile.membership?.plan || 'PROFESSIONAL',
      price: activation.amount || profile.membership?.price || 1099,
      status: isFullyActive ? 'ACTIVE' : 'PENDING',
      nextBillingDate: profile.membership?.nextBillingDate || '30 days from now',
      paymentHistory: updatedHistory,
    },
  };

  try {
    localStorage.setItem('pairup_user_profile_data', JSON.stringify(updated));
  } catch (e) {
    console.error('Failed to save to localStorage:', e);
  }

  // Sync only safe user-editable profile fields to Firestore; server-managed payment/membership
  // fields are already persisted authoritatively by verifyRazorpayPayment / razorpayWebhook.
  if (auth.currentUser && updated.email) {
    syncUserProfileToFirestore({
      uid: auth.currentUser.uid,
      name: updated.displayName || updated.name,
      email: updated.email,
      phone: updated.phone,
      city: updated.city,
      accountType: 'companion',
      avatarUrl: updated.avatarUrl,
      hourlyRate: updated.hourlyRate,
      bio: updated.bio,
    }).catch(console.warn);
  }

  return updated;
}

// =========================================================================
// PAIRING & DUAL-PAYMENT SERVICE (SRD v1.0 Section 18 & 50)
// =========================================================================
export interface PairingItem {
  id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  companion_id: string;
  companion_name: string;
  companion_email: string;
  service_type: string;
  scheduled_date: string;
  hours: number;
  total_fee: number;
  companion_fee: number;
  user_payment_status: 'PENDING' | 'SUCCESS' | 'FAILED';
  companion_payment_status: 'PENDING' | 'SUCCESS' | 'FAILED';
  pair_status: 'AWAITING_PAYMENTS' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED';
  created_at: string;
  activated_at?: string;
  notes?: string;
}

export async function fetchPairings(
  userId?: string,
  companionId?: string,
  status?: string
): Promise<{ success: boolean; pairings: PairingItem[]; summary?: any }> {
  try {
    const params = new URLSearchParams();
    if (userId) params.set('userId', userId);
    if (companionId) params.set('companionId', companionId);
    if (status) params.set('status', status);

    const res = await fetch(`/api/pairings?${params.toString()}`);
    if (res.ok) {
      return await res.json();
    }
    throw new Error('Failed to fetch pairings');
  } catch (err) {
    return { success: false, pairings: [] };
  }
}

export async function createPairingRequest(params: {
  userId: string;
  userName: string;
  userEmail: string;
  companionId: string;
  companionName: string;
  companionEmail: string;
  serviceType: string;
  scheduledDate: string;
  hours: number;
  totalFee: number;
  companionFee?: number;
}): Promise<{ success: boolean; pairing?: PairingItem; error?: string; message?: string }> {
  try {
    const res = await fetch('/api/pairings/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
    return await res.json();
  } catch (e: any) {
    return { success: false, error: e.message || 'Network error creating pairing' };
  }
}

export async function payPairingSide(
  pairId: string,
  side: 'user' | 'companion',
  paymentMethod: string = 'UPI'
): Promise<{ success: boolean; pairing?: PairingItem; isBothVerified?: boolean; message?: string; error?: string }> {
  try {
    const res = await fetch(`/api/pairings/${encodeURIComponent(pairId)}/pay`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ side, paymentMethod }),
    });
    return await res.json();
  } catch (e: any) {
    return { success: false, error: e.message || 'Payment execution failed' };
  }
}

// =========================================================================
// COMPANION REGISTRATION (SRD v1.0 Section 7)
// =========================================================================
export async function registerCompanion(formData: any): Promise<{
  success: boolean;
  user?: AdminUserRecord;
  message?: string;
  error?: string;
}> {
  try {
    const res = await fetch('/api/companion/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData),
    });
    return await res.json();
  } catch (e: any) {
    return { success: false, error: e.message || 'Failed to submit companion registration' };
  }
}

// =========================================================================
// ADMIN COMPANION & AUDIT LOGS (SRD v1.0 Section 11, 28, 36)
// =========================================================================
export interface AdminAuditLog {
  id: string;
  admin_id: string;
  admin_name: string;
  action: string;
  target_type: string;
  target_id: string;
  old_value?: string;
  new_value?: string;
  reason?: string;
  ip: string;
  timestamp: string;
}

export async function fetchAdminCompanions(): Promise<{
  success: boolean;
  summary: { total: number; pendingCount: number; activeCount: number; suspendedCount: number };
  pending: AdminUserRecord[];
  active: AdminUserRecord[];
  suspended: AdminUserRecord[];
  all: AdminUserRecord[];
}> {
  const token = getAdminToken();
  try {
    const res = await fetch('/api/admin/companions', {
      headers: { Authorization: `Bearer ${token || ''}` },
    });
    if (res.ok) return await res.json();
    throw new Error('Failed to fetch admin companions');
  } catch {
    return {
      success: false,
      summary: { total: 0, pendingCount: 0, activeCount: 0, suspendedCount: 0 },
      pending: [],
      active: [],
      suspended: [],
      all: [],
    };
  }
}

export async function verifyAdminCompanion(
  companionId: string,
  action: 'approve' | 'reject' | 'suspend',
  reason?: string
): Promise<{ success: boolean; companion?: AdminUserRecord; message?: string; error?: string }> {
  const token = getAdminToken();
  try {
    const res = await fetch(`/api/admin/companions/${encodeURIComponent(companionId)}/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token || ''}`,
      },
      body: JSON.stringify({ action, reason }),
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err.message || 'Failed to update companion verification' };
  }
}

export async function fetchAdminAuditLogs(): Promise<{ success: boolean; logs: AdminAuditLog[]; count: number }> {
  const token = getAdminToken();
  try {
    const res = await fetch('/api/admin/audit-logs', {
      headers: { Authorization: `Bearer ${token || ''}` },
    });
    if (res.ok) return await res.json();
    throw new Error('Failed to fetch audit logs');
  } catch {
    return { success: false, logs: [], count: 0 };
  }
}

// =========================================================================
// KILL CRITIC DEFENSE & RED-TEAM SIMULATION CLIENT
// =========================================================================

/**
 * Kill Critic #04, #05 & #06: Auto-Resolution Engine
 * Automatically executes timeout refund for client if companion never confirms.
 */
export async function autoResolvePairingTimeout(
  pairId: string,
  reason?: string
): Promise<{ success: boolean; auto_resolved?: boolean; pairing?: PairingItem; refund?: any; message?: string; error?: string }> {
  try {
    const res = await fetch(`/api/pairings/${encodeURIComponent(pairId)}/auto-resolve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: reason || 'Companion deadline expired. Client full refund auto-initiated.' }),
    });
    return await res.json();
  } catch (e: any) {
    return { success: false, error: e.message || 'Auto-resolution failed' };
  }
}

/**
 * Kill Critic #07: Admin Role Switching for RBAC Testing
 */
export async function switchAdminRole(
  role: 'SUPER_ADMIN' | 'FINANCE' | 'VERIFICATION' | 'SUPPORT' | 'MODERATION'
): Promise<{ success: boolean; role?: string; message?: string; error?: string }> {
  const token = getAdminToken();
  try {
    const res = await fetch('/api/admin/switch-role', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token || ''}`,
      },
      body: JSON.stringify({ role }),
    });
    return await res.json();
  } catch (e: any) {
    return { success: false, error: e.message || 'Failed to switch role' };
  }
}

export interface RedTeamSimulationResult {
  success: boolean;
  attackType: string;
  vector: string;
  clientPayload?: any;
  eventId?: string;
  idempotencyKey?: string;
  serverDefense: {
    intercepted?: boolean;
    untrustedPriceIgnored?: boolean;
    authoritativeCatalogPrice?: number;
    duplicateDetected?: boolean;
    keyFoundInStore?: boolean;
    duplicateOrderPrevented?: boolean;
    ordersCreated?: number;
    autoResolutionEngineActive?: boolean;
    rule?: string;
    refundType?: string;
    escalationBlocked?: boolean;
    httpStatus?: number;
    mandatoryReasonCheck?: string;
    auditLogGenerated?: boolean;
    immutableHistory?: string;
    defenseRule: string;
    status: string;
  };
}

/**
 * Kill Critic #14 - #52: Execute live Red-Team attack simulation against server defenses
 */
export async function simulateRedTeamAttack(
  attackType: 'PRICE_TAMPERING' | 'CALLBACK_REPLAY' | 'DOUBLE_CLICK_IDEMPOTENCY' | 'TIMEOUT_AUTO_RESOLUTION' | 'RBAC_PRIVILEGE_ESCALATION' | 'ANTI_GOD_MODE_AUDIT'
): Promise<RedTeamSimulationResult> {
  const res = await fetch('/api/security/simulate-attack', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attackType }),
  });
  return await res.json();
}


